import React, { useState, useEffect, useCallback } from 'react';
import { PageContainer, PageHeader } from '@/components/layout';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertCircle,
  CheckCircle2,
  X,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  FileText,
  RefreshCw,
} from 'lucide-react';
import {
  adminPaymentApi, adminReceiptApi, AdminPaymentItem, PaymentStatus, PlanCode,
  ConfirmPaymentResponse, ReceiptType, ReceiptIssueStatus, ReceiptIssueResult,
} from '@/api/planApi';
import { LoadingBlock, Spinner } from '@/components/ui/spinner';

// ── 상수 ─────────────────────────────────────────────────────

const PLAN_NAMES: Record<PlanCode, string> = {
  FREE: '무료',
  STARTER: '스타트',
  PROFESSIONAL: '프로',
  ENTERPRISE: '엔터프라이즈',
};

const STATUS_MAP: Record<PaymentStatus, { label: string; className: string }> = {
  PENDING: { label: '입금대기', className: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  CONFIRMED: { label: '입금 완료', className: 'bg-green-100 text-green-700 border-green-200' },
  DONE: { label: '결제 완료', className: 'bg-green-100 text-green-700 border-green-200' },
  CANCELLED: { label: '취소', className: 'bg-gray-100 text-gray-500 border-gray-200' },
};

const RECEIPT_TYPE_LABEL: Record<ReceiptType, string> = {
  CASH_RECEIPT: '현금영수증',
  TAX_INVOICE: '세금계산서',
  NONE: '발행안함',
};

/**
 * 발행 상태 라벨/색상 맵.
 * BE는 두 가지 표현을 섞어서 내려준다 - 목록/드롭다운은 entity enum 원본
 * (NOT_ISSUED/PENDING/ISSUED/FAILED/CANCELLED), 발행 결과 모달(issue/reissue/receipt-result
 * 응답의 issueStatus 필드)은 프론트용으로 매핑된 값(SUCCESS/FAILED/PENDING/CANCELLED)을 쓴다.
 * 두 값 집합을 여기 하나의 맵에서 함께 처리해 어느 쪽에서 와도 안전하게 조회한다.
 */
const STATUS_LABEL_MAP: Record<string, { label: string; className: string }> = {
  NOT_ISSUED: { label: '미발행',   className: 'bg-gray-100 text-gray-500 border-gray-200' },
  PENDING:    { label: '처리중',   className: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  ISSUED:     { label: '발행완료', className: 'bg-blue-100 text-blue-700 border-blue-200' },
  SUCCESS:    { label: '발행완료', className: 'bg-blue-100 text-blue-700 border-blue-200' },
  FAILED:     { label: '발행실패', className: 'bg-red-100 text-red-700 border-red-200' },
  CANCELLED:  { label: '취소됨',   className: 'bg-gray-100 text-gray-500 border-gray-200' },
};

const PAGE_SIZE = 20;

// ── 탭 필터 타입 ──────────────────────────────────────────────

type FilterTab = 'PENDING' | 'CONFIRMED' | 'REFUND_PENDING' | 'ALL';

const FILTER_TABS: { key: FilterTab; label: string }[] = [
  { key: 'PENDING', label: '입금대기' },
  { key: 'CONFIRMED', label: '확인완료' },
  { key: 'REFUND_PENDING', label: '환불 송금대기' },
  { key: 'ALL', label: '전체' },
];

// ── 컴포넌트 ─────────────────────────────────────────────────

export default function AdminPaymentList() {
  const [items, setItems] = useState<AdminPaymentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [activeTab, setActiveTab] = useState<FilterTab>('PENDING');
  const [processingId, setProcessingId] = useState<number | null>(null);
  /** P0-T02: confirm 성공 결과 — row 상단에 인라인 표시 */
  const [confirmResult, setConfirmResult] = useState<ConfirmPaymentResponse | null>(null);  /** 입금확인 시 증빙 자동발행 실패 경고 */
  const [receiptIssueError, setReceiptIssueError] = useState<string | null>(null);
  // ── 증빙 발행 결과 모달 ───────────────────────────────────────
  const [receiptModal, setReceiptModal] = useState<{
    open: boolean;
    item: AdminPaymentItem | null;
    result: ReceiptIssueResult | null;
    loading: boolean;
    issuing: boolean;
    error: string | null;
  }>({ open: false, item: null, result: null, loading: false, issuing: false, error: null });

  const load = useCallback(async (page: number, tab: FilterTab) => {
    setLoading(true);
    setError(null);
    try {
      const params: { page: number; size: number; status?: PaymentStatus; refundPendingOnly?: boolean } = { page, size: PAGE_SIZE };
      if (tab === 'REFUND_PENDING') {
        params.refundPendingOnly = true;
      } else if (tab !== 'ALL') {
        params.status = tab;
      }
      const res = await adminPaymentApi.getList(params);
      setItems(res.data.content ?? []);
      setTotalPages(res.data.totalPages ?? 0);
      setTotalElements(res.data.totalElements ?? 0);
      setCurrentPage(res.data.number ?? page);
    } catch {
      setError('결제 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(0, activeTab); }, [load, activeTab]);

  const handleTabChange = (tab: FilterTab) => {
    setActiveTab(tab);
    setCurrentPage(0);
  };

  const handleConfirm = async (id: number) => {
    if (!window.confirm('입금을 확인 처리하시겠습니까? 회원의 플랜이 즉시 변경됩니다.')) return;
    setProcessingId(id);
    setError(null);
    setConfirmResult(null);
    setReceiptIssueError(null);
    try {
      const res = await adminPaymentApi.confirm(id);
      const result = res.data;
      // P0-T02: row 즉시 갱신 — 전체 재조회 대신 해당 item만 CONFIRMED로 업데이트
      setItems(prev => prev.map(item =>
        item.id === id
          ? { ...item, status: 'CONFIRMED' as PaymentStatus, confirmedAt: result.appliedAt }
          : item
      ));
      setConfirmResult(result);

      // 입금 확인과 동시에 증빙 발행 시도
      try {
        const issueRes = await adminReceiptApi.issue(id);
        const issueResult = issueRes.data;
        setItems(prev => prev.map(item =>
          item.id === id
            ? {
                ...item,
                receiptIssueStatus: issueResult.receiptIssueStatus ?? item.receiptIssueStatus,
                receiptIssueNo: issueResult.issueNo ?? null,
                receiptType: issueResult.receiptType,
              }
            : item
        ));
      } catch (issueErr: any) {
        // 증빙 발행 실패는 플랜 확인과 독립적 — 비차단 처리
        setReceiptIssueError(
          issueErr?.response?.data?.message
            ?? '증빙 발행에 실패했습니다. 목록의 [발행] 버튼으로 재발행해 주세요.'
        );
      }
    } catch (err: any) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.message;
      // P0-T02: 400 = 이미 CONFIRMED, 409 = 이미 취소된 결제
      if (status === 400) {
        setError(msg ?? '이미 입금 확인 처리된 결제입니다.');
      } else if (status === 409) {
        setError(msg ?? '이미 취소된 결제건입니다. 확인 처리가 불가합니다.');
      } else {
        setError(msg ?? '입금 확인 처리에 실패했습니다.');
      }
    } finally {
      setProcessingId(null);
    }
  };

  const handleCancel = async (id: number) => {
    if (!window.confirm('결제를 취소 처리하시겠습니까?')) return;
    setProcessingId(id);
    try {
      await adminPaymentApi.cancel(id);
      await load(currentPage, activeTab);
    } catch {
      setError('결제 취소 처리에 실패했습니다.');
    } finally {
      setProcessingId(null);
    }
  };

  const handleConfirmRefund = async (id: number) => {
    if (!window.confirm('실제로 고객 계좌로 송금을 완료하셨습니까?\n확인 시 고객 화면에 "환불 완료"로 표시됩니다.')) return;
    setProcessingId(id);
    setError(null);
    try {
      const res = await adminPaymentApi.confirmRefund(id);
      const refundCompletedAt = res.data.refundCompletedAt;
      setItems(prev => prev.map(item =>
        item.id === id ? { ...item, refundCompletedAt } : item
      ));
      // 환불 송금대기 탭이면 완료 처리된 건은 목록에서 바로 제거
      if (activeTab === 'REFUND_PENDING') {
        await load(currentPage, activeTab);
      }
    } catch (err: any) {
      setError(err?.response?.data?.message ?? '환불 송금완료 처리에 실패했습니다.');
    } finally {
      setProcessingId(null);
    }
  };

  const handleOpenReceiptModal = async (item: AdminPaymentItem) => {
    setReceiptModal({ open: true, item, result: null, loading: true, issuing: false, error: null });
    try {
      const res = await adminReceiptApi.getResult(item.id);
      setReceiptModal(prev => ({ ...prev, result: res.data, loading: false }));
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 404) {
        // 아직 발행 이력 없음
        setReceiptModal(prev => ({ ...prev, loading: false }));
      } else {
        setReceiptModal(prev => ({
          ...prev, loading: false,
          error: err?.response?.data?.message ?? '발행 결과를 불러오는데 실패했습니다.',
        }));
      }
    }
  };

  const handleReceiptIssue = async (isReissue: boolean) => {
    if (!receiptModal.item) return;
    const id = receiptModal.item.id;
    setReceiptModal(prev => ({ ...prev, issuing: true, error: null }));
    try {
      const res = isReissue
        ? await adminReceiptApi.reissue(id)
        : await adminReceiptApi.issue(id);
      const result = res.data;
      // 모달 결과 및 item 동기 업데이트 (발행 상태 즉시 반영)
      setReceiptModal(prev => ({
        ...prev,
        result,
        issuing: false,
        item: prev.item
          ? { ...prev.item, receiptIssueStatus: result.receiptIssueStatus ?? prev.item.receiptIssueStatus, receiptIssueNo: result.issueNo ?? null, receiptType: result.receiptType }
          : prev.item,
      }));
      setItems(prev => prev.map(item =>
        item.id === id
          ? {
              ...item,
              receiptIssueStatus: result.receiptIssueStatus ?? item.receiptIssueStatus,
              receiptIssueNo: result.issueNo ?? null,
              receiptType: result.receiptType,
            }
          : item
      ));
    } catch (err: any) {
      setReceiptModal(prev => ({
        ...prev, issuing: false,
        error: err?.response?.data?.message ?? '증빙 발행 처리에 실패했습니다.',
      }));
    }
  };

  const handleStatusChange = async (id: number, newStatus: ReceiptIssueStatus) => {
    let issueNo: string | undefined;
    // 세금계산서는 바로빌 자동발행이 비활성 상태라 관리자가 홈택스 등에서 직접
    // 발행 후 실제 승인번호를 입력해야 함 - 번호 없이는 "발행완료"로 바꿀 수 없다.
    if (newStatus === 'ISSUED') {
      const item = items.find(i => i.id === id);
      const existing = item?.receiptIssueNo ?? '';
      const input = window.prompt('실제 발급 승인번호를 입력하세요.', existing);
      if (input === null) return; // 취소
      if (!input.trim() && !existing) {
        setError('발급 상태 변경 실패: 승인번호를 입력해야 발행완료로 변경할 수 있습니다.');
        return;
      }
      issueNo = input.trim() || undefined;
    }
    try {
      const res = await adminReceiptApi.updateStatus(id, newStatus, issueNo);
      setItems(prev => prev.map(item =>
        item.id === id
          ? { ...item, receiptIssueStatus: newStatus, receiptIssueNo: res.data.receiptIssueNo ?? item.receiptIssueNo }
          : item
      ));
    } catch (err: any) {
      setError(`발급 상태 변경 실패: ${err?.response?.data?.message ?? '알 수 없는 오류'}`);
    }
  };

  const formatDate = (iso: string) =>
    iso ? new Date(iso).toLocaleString('ko-KR', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    }) : '-';

  const formatAmount = (amount: number) => `₩${amount.toLocaleString()}`;

  // 입금대기 건수 (현재 탭 기준)
  const pendingCount = activeTab === 'PENDING' ? totalElements : null;
  const refundPendingCount = activeTab === 'REFUND_PENDING' ? totalElements : null;

  return (
    <PageContainer maxWidth="full">
      <PageHeader
        title="입금 관리"
        description="계좌이체 입금 확인 및 결제 이력을 관리합니다."
        action={
          <div className="flex gap-2">
            {pendingCount !== null && pendingCount > 0 && (
              <Badge className="bg-yellow-100 text-yellow-700 border border-yellow-300 text-sm px-3 py-1">
                입금대기 {pendingCount.toLocaleString()}건
              </Badge>
            )}
            {refundPendingCount !== null && refundPendingCount > 0 && (
              <Badge className="bg-orange-100 text-orange-700 border border-orange-300 text-sm px-3 py-1">
                환불 송금대기 {refundPendingCount.toLocaleString()}건
              </Badge>
            )}
          </div>
        }
      />

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="w-4 h-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* P0-T02: confirm 성공 결과 — appliedPlan + appliedAt + userId 표시 */}
      {confirmResult && (
        <Alert className="border-green-200 bg-green-50">
          <CheckCircle2 className="w-4 h-4 text-green-600" />
          <AlertDescription className="text-green-800 text-sm">
            입금 확인 완료 —{' '}
            <strong>{PLAN_NAMES[confirmResult.appliedPlan]}</strong> 플랜이{' '}
            <strong>{new Date(confirmResult.appliedAt).toLocaleString('ko-KR')}</strong>에 적용되었습니다.
            {' '}(회원: {confirmResult.userId})
            <button
              className="ml-3 text-green-600 underline text-xs"
              onClick={() => setConfirmResult(null)}
            >
              닫기
            </button>
          </AlertDescription>
        </Alert>
      )}

      {receiptIssueError && (
        <Alert className="border-orange-200 bg-orange-50">
          <AlertCircle className="w-4 h-4 text-orange-500" />
          <AlertDescription className="text-orange-800 text-sm flex items-center justify-between">
            <span>{receiptIssueError}</span>
            <button
              className="ml-3 text-orange-600 underline text-xs flex-shrink-0"
              onClick={() => setReceiptIssueError(null)}
            >
              닫기
            </button>
          </AlertDescription>
        </Alert>
      )}

      <Card>
        {/* 탭 필터 */}
        <CardHeader className="pb-0 px-4 pt-4">
          <div className="flex gap-1 border-b">
            {FILTER_TABS.map(tab => (
              <button
                key={tab.key}
                onClick={() => handleTabChange(tab.key)}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${
                  activeTab === tab.key
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
                {tab.key === 'PENDING' && pendingCount !== null && pendingCount > 0 && (
                  <span className="ml-1.5 bg-yellow-500 text-white text-xs rounded-full px-1.5 py-0.5">
                    {pendingCount}
                  </span>
                )}
                {tab.key === 'REFUND_PENDING' && refundPendingCount !== null && refundPendingCount > 0 && (
                  <span className="ml-1.5 bg-orange-500 text-white text-xs rounded-full px-1.5 py-0.5">
                    {refundPendingCount}
                  </span>
                )}
              </button>
            ))}
          </div>
        </CardHeader>

        <CardContent className="p-0">
          {loading ? (
            <LoadingBlock size="lg" py={16} />
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>결제월</TableHead>
                    <TableHead>회원</TableHead>
                    <TableHead>아이디</TableHead>
                    <TableHead>팀(직책)</TableHead>
                    <TableHead>플랜</TableHead>
                    <TableHead className="text-right">금액</TableHead>
                    <TableHead>결제방식</TableHead>
                    <TableHead>입금자명</TableHead>
                    <TableHead>요청일시</TableHead>
                    <TableHead>상태</TableHead>
                    <TableHead>증빙 발행</TableHead>
                    <TableHead className="text-center">처리</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={11} className="text-center text-gray-400 py-16 text-sm">
                        {activeTab === 'PENDING'
                          ? '입금 대기 중인 건이 없습니다.'
                          : activeTab === 'REFUND_PENDING'
                          ? '송금 대기 중인 환불 건이 없습니다.'
                          : '결제 이력이 없습니다.'}
                      </TableCell>
                    </TableRow>
                  ) : items.map(item => {
                    const isRefundPendingTransfer = item.status === 'CANCELLED' && item.paymentMethod === 'BANK_TRANSFER'
                      && !!item.refundAmount && !item.refundCompletedAt;
                    const status = isRefundPendingTransfer
                      ? { label: '환불 송금대기', className: 'bg-orange-100 text-orange-700 border-orange-200' }
                      : STATUS_MAP[item.status];
                    const isProcessing = processingId === item.id;
                    const receiptIssued = item.cashReceiptIssued === true || Boolean(item.cashReceiptIssuedAt) || Boolean(item.cashReceiptNo);
                    // 증빙 발행 상태 (신규 필드 우선, 없으면 구 cashReceipt 필드로 fallback)
                    const issueStatus: ReceiptIssueStatus | null =
                      item.receiptIssueStatus ?? (receiptIssued ? 'ISSUED' : null);
                    const hasReceiptSetting = item.receiptType != null && item.receiptType !== 'NONE';
                    const issueFailed = issueStatus === 'FAILED';
                    const issueNotDone = !issueStatus || issueStatus === 'NOT_ISSUED';
                    return (
                      <TableRow key={item.id}>
                        <TableCell className="font-medium">{item.billingMonth}</TableCell>
                        <TableCell className="font-medium">{item.userName}</TableCell>
                        <TableCell className="text-gray-500 text-xs">{item.userId}</TableCell>
                        <TableCell className="text-xs">
                          {item.teamName ? (
                            <span className="text-gray-700">
                              {item.teamName}
                              <span className="text-gray-500 ml-1">
                                ({item.teamRole === 'OWNER' ? '팀장' : '팀원'})
                              </span>
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </TableCell>
                        <TableCell>{PLAN_NAMES[item.plan]}</TableCell>
                        <TableCell className="text-right font-semibold">{formatAmount(item.amount)}</TableCell>
                        <TableCell className="text-gray-500">
                          {item.paymentMethod === 'CARD' ? '카드' : '계좌이체'}
                        </TableCell>
                        <TableCell className="text-gray-600">{item.depositorName ?? '-'}</TableCell>
                        <TableCell className="text-gray-500 text-xs">{formatDate(item.createdAt)}</TableCell>
                        <TableCell>
                          <Badge className={`text-xs border ${status.className}`}>{status.label}</Badge>
                        </TableCell>
                        {/* 증빙 발행 셀 */}
                        <TableCell>
                          <div className="flex flex-col gap-1 min-w-[90px]">
                            {/* 증빙 유형 badge */}
                            {item.receiptType ? (
                              item.receiptType === 'NONE' ? (
                                <span className="text-xs text-gray-400">발행안함</span>
                              ) : (
                                <span className="text-xs font-medium text-gray-700">
                                  {RECEIPT_TYPE_LABEL[item.receiptType]}
                                </span>
                              )
                            ) : (
                              <span className="text-xs text-gray-400">미설정</span>
                            )}
                            {/* 발행 상태 badge 및 드롭다운 */}
                            {hasReceiptSetting && (
                              <div className="flex items-center gap-1">
                                {issueStatus && STATUS_LABEL_MAP[issueStatus] && (
                                  <Badge className={`text-[11px] border w-fit ${STATUS_LABEL_MAP[issueStatus].className}`}>
                                    {STATUS_LABEL_MAP[issueStatus].label}
                                  </Badge>
                                )}
                                <Select
                                  value={issueStatus ?? 'NOT_ISSUED'}
                                  onValueChange={(value: ReceiptIssueStatus) => handleStatusChange(item.id, value)}
                                >
                                  <SelectTrigger className="h-6 text-[10px] px-1 w-20">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="NOT_ISSUED">미발행</SelectItem>
                                    <SelectItem value="PENDING">처리중</SelectItem>
                                    <SelectItem value="ISSUED">발행완료</SelectItem>
                                    <SelectItem value="FAILED">발행실패</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            )}
                            {item.cashReceiptIssuedAt && !item.receiptType && (
                              <span className="text-[11px] text-gray-400">{formatDate(item.cashReceiptIssuedAt)}</span>
                            )}
                            {/* 발행 결과 / 발행 버튼 */}
                            {item.status === 'CONFIRMED' && (
                              item.receiptType && item.receiptType !== 'NONE' ? (
                                <div className="flex gap-1 mt-0.5">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-6 text-[11px] px-2"
                                    onClick={() => handleOpenReceiptModal(item)}
                                  >
                                    <FileText className="w-3 h-3 mr-0.5" />
                                    결과
                                  </Button>
                                  {(issueFailed || issueNotDone) && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="h-6 text-[11px] px-2 text-blue-600 border-blue-200 hover:bg-blue-50"
                                      onClick={() => handleOpenReceiptModal(item)}
                                    >
                                      <RefreshCw className="w-3 h-3 mr-0.5" />
                                      발행
                                    </Button>
                                  )}
                                </div>
                              ) : !item.receiptType ? (
                                <span className="text-[11px] text-gray-400">설정 필요</span>
                              ) : null
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {item.status === 'PENDING' ? (
                            <div className="flex items-center justify-center gap-1.5">
                              <Button
                                size="sm"
                                className="h-7 text-xs bg-green-600 hover:bg-green-700"
                                disabled={isProcessing}
                                onClick={() => handleConfirm(item.id)}
                              >
                                {isProcessing ? <Spinner size="3.5" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                                <span className="ml-1">입금확인</span>
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs text-red-500 border-red-200 hover:bg-red-50"
                                disabled={isProcessing}
                                onClick={() => handleCancel(item.id)}
                              >
                                <X className="w-3.5 h-3.5" />
                                <span className="ml-1">취소</span>
                              </Button>
                            </div>
                          ) : isRefundPendingTransfer ? (
                            <div className="flex flex-col items-center gap-1">
                              <span className="text-[11px] text-gray-400">
                                환불접수: {item.refundedAt ? formatDate(item.refundedAt) : '-'}
                              </span>
                              <Button
                                size="sm"
                                className="h-7 text-xs bg-orange-600 hover:bg-orange-700"
                                disabled={isProcessing}
                                onClick={() => handleConfirmRefund(item.id)}
                              >
                                {isProcessing ? <Spinner size="3.5" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                                <span className="ml-1">송금완료</span>
                              </Button>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400 block text-center">
                              {item.refundCompletedAt
                                ? `송금완료: ${formatDate(item.refundCompletedAt)}`
                                : item.confirmedAt ? formatDate(item.confirmedAt) : '-'}
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              </div>

              {/* 페이지네이션 */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t">
                  <span className="text-xs text-gray-400">총 {totalElements.toLocaleString()}건</span>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={currentPage === 0}
                      onClick={() => load(currentPage - 1, activeTab)}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-sm text-gray-500">{currentPage + 1} / {totalPages}</span>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={currentPage >= totalPages - 1}
                      onClick={() => load(currentPage + 1, activeTab)}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* 증빙 발행 결과 모달 */}
      <Dialog
        open={receiptModal.open}
        onOpenChange={open => setReceiptModal(prev => ({ ...prev, open }))}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              증빙 발행 결과
            </DialogTitle>
          </DialogHeader>

          {receiptModal.loading && (
            <LoadingBlock size="lg" py={8} />
          )}

          {!receiptModal.loading && (
            <div className="space-y-4">
              {receiptModal.error && (
                <Alert variant="destructive">
                  <AlertCircle className="w-4 h-4" />
                  <AlertDescription>{receiptModal.error}</AlertDescription>
                </Alert>
              )}

              {receiptModal.item && (
                <div className="bg-gray-50 rounded-lg p-3 text-sm space-y-1.5">
                  <div className="flex justify-between">
                    <span className="text-gray-500">결제월</span>
                    <span className="font-medium">{receiptModal.item.billingMonth}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">회원</span>
                    <span className="font-medium">{receiptModal.item.userName} ({receiptModal.item.userId})</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">증빙 유형</span>
                    <span className="font-medium">
                      {receiptModal.item.receiptType
                        ? RECEIPT_TYPE_LABEL[receiptModal.item.receiptType]
                        : receiptModal.result?.receiptType
                          ? RECEIPT_TYPE_LABEL[receiptModal.result.receiptType]
                          : '-'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500">발행 상태</span>
                    {(() => {
                      const st = receiptModal.result?.issueStatus ?? receiptModal.item.receiptIssueStatus;
                      return st && STATUS_LABEL_MAP[st]
                        ? <Badge className={`text-xs border ${STATUS_LABEL_MAP[st].className}`}>{STATUS_LABEL_MAP[st].label}</Badge>
                        : <span className="font-medium text-gray-400">미발행</span>;
                    })()}
                  </div>
                </div>
              )}

              {/* 발행 결과 */}
              {receiptModal.result ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">발행 상태</span>
                    <Badge className={`text-xs border ${(STATUS_LABEL_MAP[receiptModal.result.issueStatus] ?? STATUS_LABEL_MAP['NOT_ISSUED']).className}`}>
                      {(STATUS_LABEL_MAP[receiptModal.result.issueStatus] ?? STATUS_LABEL_MAP['NOT_ISSUED']).label}
                    </Badge>
                  </div>

                  {receiptModal.result.issueStatus === 'SUCCESS' && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm space-y-1.5">
                      <div className="flex justify-between">
                        <span className="text-gray-500">승인번호</span>
                        <span className="font-medium font-mono">{receiptModal.result.issueNo ?? '-'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">발행일시</span>
                        <span className="font-medium">
                          {receiptModal.result.issuedAt
                            ? new Date(receiptModal.result.issuedAt).toLocaleString('ko-KR')
                            : '-'}
                        </span>
                      </div>
                    </div>
                  )}

                  {receiptModal.result.issueStatus === 'FAILED' && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm">
                      <p className="text-red-700 font-medium mb-1">실패 사유</p>
                      <p className="text-red-600">{receiptModal.result.failReason ?? '알 수 없는 오류'}</p>
                    </div>
                  )}
                </div>
              ) : (
                !receiptModal.error && (
                  <p className="text-sm text-gray-500 text-center py-2">아직 발행 이력이 없습니다.</p>
                )
              )}

              {/* 발행 / 재발행 버튼 */}
              {!receiptModal.error && receiptModal.item?.status === 'CONFIRMED' && (
                receiptModal.result?.issueStatus === 'SUCCESS' ? null : (
                  <div className="flex justify-end pt-2 border-t">
                    <Button
                      onClick={() => handleReceiptIssue(receiptModal.result?.issueStatus === 'FAILED')}
                      disabled={receiptModal.issuing}
                      className="gap-1.5"
                    >
                      {receiptModal.issuing
                        ? <Spinner size="sm" />
                        : <RefreshCw className="w-4 h-4" />}
                      {receiptModal.result?.issueStatus === 'FAILED' ? '재발행 요청' : '발행 요청'}
                    </Button>
                  </div>
                )
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
